/**
 * Missions — field tasks with a planned route and assigned phones.
 * A mission may be linked to an Intel Packet or stand on its own.
 */
import axios from 'axios';

const baseUrl = process.env.REACT_APP_API_URL || '/api';

const getAuthHeaders = () => ({
  Authorization: `Bearer ${localStorage.getItem('access_token') || ''}`,
});

/**
 * `baseline` was `tasked`. The old name described paperwork and told an
 * operator nothing to do; the new one names the only window in which the
 * convoy's own radios can be learned — parked, formed up, not yet moving.
 */
export type MissionStatus = 'planned' | 'baseline' | 'in_progress' | 'complete' | 'cancelled';

export const MISSION_STATUS_LABELS: Record<MissionStatus, string> = {
  planned: 'Planned',
  baseline: 'Baseline',
  in_progress: 'In progress',
  complete: 'Complete',
  cancelled: 'Cancelled',
};

export const MISSION_STATUS_COLOR: Record<MissionStatus, string> = {
  planned: '#8f9bb3',
  baseline: '#f5b942',
  in_progress: '#42a5f5',
  complete: '#66bb6a',
  cancelled: '#8f8f8f',
};

/** A rehearsal collects exactly as a live run does, and pages nobody. */
export type MissionRunKind = 'rehearsal' | 'live';

export const RUN_KIND_LABELS: Record<MissionRunKind, string> = {
  rehearsal: 'Rehearsal',
  live: 'Live run',
};

/** One occasion on which the route was driven. */
export interface MissionRun {
  id: number;
  mission_id: number;
  run_number: number;
  kind: MissionRunKind;
  state: 'active' | 'complete' | 'abandoned';
  started_at: string;
  ended_at?: string | null;
  started_by?: string | null;
  ended_by?: string | null;
  baseline_started_at?: string | null;
  baseline_ready_at?: string | null;
  /** What the run averaged, and what the next one plans against. */
  observed_speed_mps?: number | null;
  distance_m?: number | null;
  duration_s?: number | null;
  /** The line it was driven on. A replan invalidates what it measured. */
  route_signature?: string | null;
  notes?: string | null;
  /** Corridor regulars learned, present only on the response that ends a run. */
  corridor_learned?: number;
  /**
   * Sentinel's current read, present only when live narration is enabled.
   * Overwritten each time: it describes now, not the run.
   */
  live_read?: { read: string; attention: 'none' | 'watch' | 'act' } | null;
  live_read_at?: string | null;
}

export interface MissionWaypoint {
  id: number;
  mission_id: number;
  seq: number;
  label?: string | null;
  latitude: number;
  longitude: number;
  notes?: string | null;
  /** Where this stop falls along the planned line. Null until the route is set. */
  distance_along_m?: number | null;
  /** Stamped once, when the lead device first passes it. */
  reached_at?: string | null;
}

export interface MissionAssignee {
  id: number;
  mission_id: number;
  device_id: string;
  label?: string | null;
  entity_id?: string | null;
  device_kind?: 'phone' | 'wardriver' | 'watch' | 'drone' | 'vehicle' | null;
  /** Whether this device collects RF. Only observers can detect a follower. */
  is_observer?: boolean;
  /** A fixed collection site: it collects, but its position never drives the route. */
  is_stationary?: boolean;
}

export interface RosterDevice {
  device_id: string;
  kind: 'phone' | 'wardriver' | 'watch' | 'drone' | 'vehicle';
  is_observer: boolean;
}

/** An entity and everything it carries, which is the unit of assignment. */
export interface RosterEntity {
  entity_id: string;
  entity_type?: string | null;
  course_id?: string | null;
  devices: RosterDevice[];
  observer_count: number;
}

export interface MissionGeofence {
  id: number;
  mission_id: number;
  geofence_id: number;
  name: string;
  geometry?: { type: string; coordinates: number[][][] } | null;
}

export interface MissionGeofenceEvent {
  id: number;
  geofence_id: number;
  geofence_name: string;
  device_id: string;
  entity_id?: string | null;
  event_type: 'enter' | 'exit';
  latitude: number;
  longitude: number;
  timestamp: string;
}

export interface MissionRouteStats {
  distance?: number;
  duration?: number;
  traffic_delay?: number;
  profile?: string;
}

/**
 * A second route, planned and scanned alongside the first.
 *
 * Its worth is that somebody looked at it in advance. Switching to it mid-run
 * is acting on a decision made calmly, which is precisely what a route computed
 * under pressure cannot offer.
 */
export interface MissionNamedRoute {
  id: string;
  label: string;
  waypoints?: Array<{
    label?: string | null;
    latitude: number;
    longitude: number;
    notes?: string | null;
  }>;
  geometry?: GeoJSON.LineString | null;
  stats?: MissionRouteStats | null;
  profile?: string | null;
  options?: Record<string, unknown> | null;
  alternate?: MissionAlternateRoute | null;
  saved_at?: string | null;
}

export interface MissionAlternateRoute {
  geometry: GeoJSON.LineString;
  stats?: MissionRouteStats | null;
  profile?: string | null;
  options?: Record<string, unknown> | null;
  intel?: Record<string, any> | null;
  label?: string | null;
  /** The alternate's own stops. A mid-run switch rejoins onto these. */
  waypoints?: Array<{ label?: string | null; latitude: number; longitude: number; notes?: string | null }>;
  signature?: string | null;
  saved_at?: string | null;
}

export interface Mission {
  id: number;
  packet_id?: number | null;
  packet_name?: string | null;
  name: string;
  status: MissionStatus;
  objective?: string | null;
  route_profile?: string | null;
  route_options?: Record<string, unknown> | null;
  route_geometry?: GeoJSON.LineString | null;
  route_stats?: MissionRouteStats | null;
  /** The pre-vetted second route, if one was planned. */
  alt_route?: MissionAlternateRoute | null;
  /** The line we just left after a swap or Sentinel reroute. */
  prior_route?: MissionAlternateRoute | null;
  /** Independent named plans (Secondary, Third, …) with their own stops. */
  saved_routes?: MissionNamedRoute[] | null;
  /** Which saved plan is currently copied onto the live columns. */
  active_route_id?: string | null;
  alerts_enabled?: boolean;
  deviation_threshold_m?: number;
  /**
   * Whether Sentinel narrates the run as it happens. Off by default: the
   * deterministic comparison already answers the live questions, and a model on
   * a timer costs money to restate an unchanged picture.
   */
  sentinel_live_enabled?: boolean;
  started_at?: string | null;
  completed_at?: string | null;
  created_by: string;
  created_at: string;
  updated_at: string;
  /** Planner findings kept with the mission. See the route planner README. */
  route_intel?: Record<string, any> | null;
  /** Server's verdict on whether `route_intel` still describes the current line. */
  route_intel_stale?: boolean;
  waypoints: MissionWaypoint[];
  assignees: MissionAssignee[];
  geofences: MissionGeofence[];
  /** The run under way, if any. Null between runs. */
  current_run?: MissionRun | null;
  /** Newest first. A rehearsal stays here after the mission moves on. */
  runs?: MissionRun[];
}

export type MissionRouteEventType =
  | 'off_route'
  | 'back_on_route'
  | 'signal_lost'
  | 'signal_restored'
  | 'waypoint_reached'
  // The threat watch has always emitted these; they were missing here, so they
  // reached the panel as raw strings with no label and no colour.
  | 'cotraveler_suspected'
  | 'cotraveler_confirmed'
  | 'cotraveler_sustained'
  | 'watchlist_on_route'
  | 'corridor_threat'
  | 'wifi_located'
  | 'ble_located'
  | 'cell_located'
  | 'surveillance_ahead'
  | 'uas_intercept'
  | 'route_changed'
  | 'threat_reacquired'
  | 'threat_stale'
  | 'field_report'
  | 'rf_multiloc'
  | 'operator_alert';

export const ROUTE_EVENT_LABELS: Record<MissionRouteEventType, string> = {
  off_route: 'Off route',
  back_on_route: 'Back on route',
  signal_lost: 'Signal lost',
  signal_restored: 'Signal restored',
  waypoint_reached: 'Reached',
  cotraveler_suspected: 'Possible follower',
  cotraveler_confirmed: 'Follower confirmed',
  cotraveler_sustained: 'Following since',
  watchlist_on_route: 'Watchlisted on route',
  corridor_threat: 'Corridor threat',
  wifi_located: 'Wi-Fi located',
  ble_located: 'BLE located',
  cell_located: 'Cell located',
  surveillance_ahead: 'Surveillance ahead',
  uas_intercept: 'Drone sighted',
  route_changed: 'Route changed',
  threat_reacquired: 'Threat back',
  threat_stale: 'Threat cleared',
  field_report: 'Field report',
  rf_multiloc: 'Heard in several places',
  operator_alert: 'To vehicle',
};

export interface MissionRouteEvent {
  id: number;
  mission_id: number;
  device_id: string;
  label?: string | null;
  event_type: MissionRouteEventType;
  latitude?: number | null;
  longitude?: number | null;
  distance_m?: number | null;
  segment_index?: number | null;
  fix_at?: string | null;
  detected_at: string;
  acknowledged_at?: string | null;
  acknowledged_by?: string | null;
  meta?: Record<string, unknown> | null;
  /** Stable identity for a tracked threat across ticks. */
  threat_key?: string | null;
  severity?: string | null;
  run_id?: number | null;
  /** Which run produced this, so a drill's alerts can never read as the day's. */
  run_kind?: MissionRunKind | null;
  run_number?: number | null;
}

export interface MissionDeviceState {
  mission_id: number;
  device_id: string;
  label?: string | null;
  /** The operator this device belongs to. Null for a directly assigned device. */
  entity_id?: string | null;
  device_kind?: string | null;
  /** Fixed collection site: signal is watched, the route machine is not run. */
  is_stationary?: boolean;
  route_state: 'on_route' | 'off_route' | 'unknown';
  signal_state: 'live' | 'lost' | 'unknown';
  consecutive_off: number;
  distance_m?: number | null;
  latitude?: number | null;
  longitude?: number | null;
  last_fix_at?: string | null;
  /** How far down the route this device is, as opposed to how far off it. */
  distance_along_m?: number | null;
  speed_mps?: number | null;
  /**
   * observed = measured from recent fixes; rehearsed = a pace this convoy held
   * on a previous run of this line; planned = the routing engine's average;
   * stopped.
   */
  speed_source?: 'observed' | 'rehearsed' | 'planned' | 'stopped' | null;
  eta_at?: string | null;
  changed_at: string;
  updated_at: string;
}

/** Where the run has got to, taken from the device furthest along the route. */
export interface MissionProgress {
  lead_device_id?: string | null;
  distance_along_m: number;
  total_distance_m: number;
  remaining_m: number;
  /** 0-1. */
  progress_ratio: number;
  speed_mps?: number | null;
  speed_source?: 'observed' | 'planned' | 'stopped' | null;
  stopped: boolean;
  eta_at?: string | null;
  /** Departure plus the planned duration, for comparison against the estimate. */
  planned_eta_at?: string | null;
  /** Seconds later than planned; negative means ahead. */
  delay_s?: number | null;
  started_at?: string | null;
  elapsed_s?: number | null;
  devices_live: number;
}

/** One stop, with an actual arrival behind the convoy or a projection ahead. */
export interface MissionTimelineStop {
  id: number;
  seq: number;
  label?: string | null;
  latitude?: number | null;
  longitude?: number | null;
  distance_along_m?: number | null;
  reached_at?: string | null;
  eta_at?: string | null;
}

export interface MissionRouteStatus {
  events: MissionRouteEvent[];
  devices: MissionDeviceState[];
  progress?: MissionProgress | null;
  timeline?: MissionTimelineStop[];
  /** The run under way, so the panel can say a rehearsal is in progress. */
  run?: MissionRun | null;
  checked?: number;
  reason?: string | null;
  new_events?: number;
  /** Server-owned ladder from the brief. The panel must not recompute it. */
  threat_level?: 'clear' | 'watch' | 'elevated' | 'high' | 'critical' | null;
  threat_level_reason?: string | null;
  threat_level_reason_short?: string | null;
  threat_level_at?: string | null;
}

export interface MissionWaypointInput {
  label?: string;
  latitude: number;
  longitude: number;
  notes?: string;
}

export interface MissionListParams {
  packet_id?: number;
  standalone?: boolean;
  status?: MissionStatus;
}

export async function listMissions(params?: MissionListParams): Promise<Mission[]> {
  const response = await axios.get(`${baseUrl}/missions`, { params, headers: getAuthHeaders() });
  return response.data;
}

/**
 * Enough to draw a row in a list, and nothing else.
 *
 * The full mission carries its route geometry and corridor scan, which a list
 * never reads and which are most of the bytes — a 300 km mission is a quarter
 * of a megabyte, almost all of it coordinates. Anything that needs the whole
 * thing is opening one mission and should fetch that one.
 */
export interface MissionSummary {
  id: number;
  packet_id?: number | null;
  packet_name?: string | null;
  name: string;
  objective?: string | null;
  status: MissionStatus;
  route_profile?: string | null;
  route_stats?: MissionRouteStats | null;
  has_route: boolean;
  has_alternate: boolean;
  waypoint_count: number;
  assignee_count: number;
  geofence_count: number;
  alerts_enabled?: boolean;
  started_at?: string | null;
  completed_at?: string | null;
  created_by: string;
  created_at: string;
  updated_at: string;
}

export async function listMissionSummaries(
  params?: MissionListParams,
): Promise<MissionSummary[]> {
  const response = await axios.get(`${baseUrl}/missions/summary`, {
    params,
    headers: getAuthHeaders(),
  });
  return response.data;
}

export async function getMission(missionId: number): Promise<Mission> {
  const response = await axios.get(`${baseUrl}/missions/${missionId}`, { headers: getAuthHeaders() });
  return response.data;
}

export async function createMission(body: {
  name: string;
  objective?: string;
  packet_id?: number;
}): Promise<Mission> {
  const response = await axios.post(`${baseUrl}/missions`, body, { headers: getAuthHeaders() });
  return response.data;
}

/** Turn off live narration without waiting — used when leaving the page. */
export function stopSentinelLive(missionId: number): void {
  const token = localStorage.getItem('access_token') || '';
  fetch(`${baseUrl}/missions/${missionId}`, {
    method: 'PATCH',
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ sentinel_live_enabled: false }),
    keepalive: true,
  }).catch(() => undefined);
}

export async function updateMission(
  missionId: number,
  body: {
    name?: string;
    status?: MissionStatus;
    objective?: string;
    packet_id?: number;
    unlink_packet?: boolean;
    alerts_enabled?: boolean;
    deviation_threshold_m?: number;
    sentinel_live_enabled?: boolean;
  },
): Promise<Mission> {
  const response = await axios.patch(`${baseUrl}/missions/${missionId}`, body, { headers: getAuthHeaders() });
  return response.data;
}

export async function deleteMission(missionId: number): Promise<void> {
  await axios.delete(`${baseUrl}/missions/${missionId}`, { headers: getAuthHeaders() });
}

export async function replaceMissionWaypoints(
  missionId: number,
  waypoints: MissionWaypointInput[],
): Promise<Mission> {
  const response = await axios.put(`${baseUrl}/missions/${missionId}/waypoints`, waypoints, { headers: getAuthHeaders() });
  return response.data;
}

/**
 * Assign entities, or bare devices, to a mission.
 *
 * Prefer `entity_id`: the backend expands it to every device on that entity's
 * roster row, so the vehicle beacon and the phone riding in the same vehicle
 * cannot be assigned apart.
 */
export async function replaceMissionAssignees(
  missionId: number,
  assignees: Array<{ device_id?: string; entity_id?: string; label?: string; stationary?: boolean }>,
): Promise<Mission> {
  const response = await axios.put(`${baseUrl}/missions/${missionId}/assignees`, assignees, { headers: getAuthHeaders() });
  return response.data;
}

export async function listRosterEntities(): Promise<RosterEntity[]> {
  const response = await axios.get(`${baseUrl}/missions/roster/entities`, { headers: getAuthHeaders() });
  return response.data;
}

export async function replaceMissionGeofences(
  missionId: number,
  geofenceIds: number[],
): Promise<Mission> {
  const response = await axios.put(
    `${baseUrl}/missions/${missionId}/geofences`,
    geofenceIds.map((geofence_id) => ({ geofence_id })),
    { headers: getAuthHeaders() },
  );
  return response.data;
}

export async function getMissionGeofenceActivity(
  missionId: number,
  hours = 24,
): Promise<MissionGeofenceEvent[]> {
  const response = await axios.get(`${baseUrl}/missions/${missionId}/geofence-activity`, {
    params: { hours },
    headers: getAuthHeaders(),
  });
  return response.data;
}

/**
 * Begin a baseline (rehearsal) or the live run.
 *
 * A rehearsal runs under the Baseline status, the live run under In progress;
 * the monitor watches both. Only the live one enrols the convoy for
 * dissemination, which is what keeps a drill from paging anybody.
 */
export async function startMissionRun(
  missionId: number,
  kind: MissionRunKind,
): Promise<Mission> {
  const response = await axios.post(
    `${baseUrl}/missions/${missionId}/runs`,
    { kind },
    { headers: getAuthHeaders() },
  );
  return response.data;
}

/** One stop's elapsed time against the same stop on the reference run. */
export interface ComparedStop {
  waypoint_id: number;
  seq?: number | null;
  label?: string | null;
  elapsed_s?: number | null;
  reference_elapsed_s?: number | null;
  /** Positive is slower than the reference. Null when only one run reached it. */
  delta_s?: number | null;
}

export interface ComparedEmitter {
  identifier: string;
  normalized_identifier: string;
  name?: string | null;
  signal_type: string;
  segment_count?: number | null;
  first_segment?: number | null;
  last_segment?: number | null;
  sighting_count?: number | null;
}

/**
 * Something the run should have recorded and did not.
 *
 * The point of reporting these is that "we found nothing" and "we could not
 * look" are indistinguishable in an event log and demand opposite responses.
 */
export interface CollectionGap {
  kind:
    | 'no_rf_observer'
    | 'signal_loss'
    | 'rf_coverage'
    | 'stops_not_reached'
    | 'no_route_intel'
    | 'stale_route_intel';
  severity: 'critical' | 'warning' | 'info';
  detail: string;
  count?: number;
  devices?: string[];
  spans?: { from_segment: number; to_segment: number; length: number }[];
}

export interface MissionRunComparison {
  subject: { run: MissionRun; label: string };
  reference?: { run: MissionRun; label: string } | null;
  pace?: {
    stops: ComparedStop[];
    not_yet_reached: number;
    worst_stop?: ComparedStop | null;
    subject_speed_mps?: number | null;
    reference_speed_mps?: number | null;
  };
  emitters?: {
    new: ComparedEmitter[];
    new_count: number;
    absent: ComparedEmitter[];
    absent_count: number;
    shared_count: number;
    subject_total: number;
    reference_total: number;
  };
  threats?: {
    new: { threat_key: string; kind: string; state: string; score?: number | null }[];
    new_count: number;
    repeated_count: number;
  };
  gaps: CollectionGap[];
  /** Present when there was no run worth comparing against. */
  reason?: string;
  /** Completed runs the operator can pick as a different reference. */
  available: MissionRun[];
}

/**
 * This run against another, plus what it failed to collect.
 *
 * Pure arithmetic on the server — no model is consulted — so it is cheap enough
 * to ask for during a run, which is when being nine minutes down on the
 * rehearsal is still worth knowing.
 */
export async function compareMissionRuns(
  missionId: number,
  params: { run_id?: number; reference_id?: number } = {},
): Promise<MissionRunComparison> {
  const response = await axios.get(`${baseUrl}/missions/${missionId}/compare`, {
    params,
    headers: getAuthHeaders(),
  });
  return response.data;
}

/**
 * How far something has travelled with the convoy, banded.
 *
 * The score says whether an emitter is a co-traveller at all and saturates once
 * it has. In city traffic that leaves a dozen of them sitting on the threshold,
 * and this is what tells them apart: distance shared, because an hour in
 * gridlock puts the same cars around you and proves nothing, while forty
 * kilometres is a decision at every junction.
 */
export type CompanionTier = 'brief' | 'persistent' | 'sustained' | 'entrenched';

export const TIER_LABELS: Record<CompanionTier, string> = {
  brief: 'Brief',
  persistent: 'Persistent',
  sustained: 'Sustained',
  entrenched: 'Entrenched',
};

export type CotravelerState = 'none' | 'suspected' | 'confirmed' | 'sustained' | 'stale';

export interface MissionCotraveler {
  threat_key: string;
  identifier?: string | null;
  name?: string | null;
  signal_type?: string | null;
  manufacturer_id?: number | null;
  state: CotravelerState;
  score?: number | null;
  tier: CompanionTier;
  shared_distance_m?: number | null;
  shared_seconds?: number | null;
  sighting_count?: number | null;
  distinct_segments?: number | null;
  distinct_time_windows?: number | null;
  segment_index?: number | null;
  latitude?: number | null;
  longitude?: number | null;
  /** Set when a rehearsal of this route already established it as normal traffic. */
  corridor_regular_runs?: number | null;
  /** Still here after the route deliberately changed — the strongest reading available. */
  survived_reroute?: boolean;
  /** New identifier stitched to a confirmed follower that went quiet mid-route. */
  gap_fill?: boolean;
  continued_from?: string | null;
  continued_from_label?: string | null;
  watchlist_id?: number | null;
  watchlist_label?: string | null;
  track?: string | null;
  observer_device_ids?: string[];
  first_seen?: string | null;
  last_seen?: string | null;
  last_observed?: string | null;
  stale_since?: string | null;
  run_id?: number | null;
}

/**
 * Everything moving with the convoy, most credible first — not just what raised
 * an alert. The alert list answers "what should I look at"; this answers the
 * question underneath it.
 */
export async function listCotravelers(
  missionId: number,
  includeCleared = false,
): Promise<MissionCotraveler[]> {
  const response = await axios.get(`${baseUrl}/missions/${missionId}/cotravelers`, {
    params: { include_cleared: includeCleared },
    headers: getAuthHeaders(),
  });
  return response.data?.cotravelers || [];
}

/**
 * Watchlist a co-traveller straight from the panel.
 *
 * The event-based promote needs an alert to point at, and the emitter worth
 * promoting is often the one sitting just below the alert threshold on a long
 * shared run — which is exactly what the panel exists to surface.
 */
export async function promoteCotraveler(
  missionId: number,
  threatKey: string,
  reason?: string,
): Promise<{ watchlist_id: number; already_present: boolean; label: string }> {
  const response = await axios.post(
    `${baseUrl}/missions/${missionId}/cotravelers/${encodeURIComponent(threatKey)}/promote`,
    { reason },
    { headers: getAuthHeaders() },
  );
  return response.data;
}

// ------------------------------------------------------- Sentinel conversation

/**
 * What Sentinel can be asked to actually do.
 *
 * A closed set, and the buttons are the only way in. The difference between
 * "go around that" and "find out whether we are being followed" is a tactical
 * judgement with different risks, so it is made by pressing a labelled control
 * rather than inferred from a sentence someone typed in a hurry.
 */
export type SentinelAction =
  | 'route_around_threats'
  | 'detection_route'
  | 'avoid_watchlist'
  | 'rejoin_route';

export const SENTINEL_ACTION_LABELS: Record<SentinelAction, string> = {
  route_around_threats: 'Route around threats',
  detection_route: 'Are we being followed?',
  avoid_watchlist: 'Avoid watchlisted',
  rejoin_route: 'Get back on route',
};

export const SENTINEL_ACTION_HINTS: Record<SentinelAction, string> = {
  route_around_threats:
    'Asks the routing engine for ways past everything currently raised, ranked by what they clear.',
  detection_route:
    'Builds a route with several deliberate turns. Anything still with us at the end of it is '
    + 'following us rather than the road — and it may shake them off either way.',
  avoid_watchlist:
    'Routes clear of emitters already known hostile that are sitting on this road.',
  rejoin_route:
    'Two ways back for a convoy that has strayed: onto the planned line, which is the road that '
    + 'was scanned, or straight to the next stop, which is quicker on roads nobody has looked at.',
};

/**
 * What each button does to the route, in two words.
 *
 * They read alike at a glance — all of them are the convoy going a different
 * way — and the distinction that matters under time pressure is whether you
 * are dodging something, ruling somewhere out, asking a question of whoever is
 * behind you, or undoing a wrong turn.
 */
export const SENTINEL_ACTION_KINDS: Record<SentinelAction, string> = {
  route_around_threats: 'Reroute',
  detection_route: 'Surveillance detection',
  avoid_watchlist: 'Route constraint',
  rejoin_route: 'Correction',
};

/** True of every action: they return options, and a person picks one. */
export const SENTINEL_ACTION_CAVEAT = 'Returns options — nothing moves until you take one';

export interface SentinelFollower {
  label?: string | null;
  state?: string | null;
  watchlisted?: boolean;
  distance_from_lead_m?: number | null;
  relation?: string | null;
  distinct_segments?: number | null;
  shared_distance_m?: number | null;
  last_detected?: string | null;
  survived_reroute?: boolean;
}

export interface MissionChatTurn {
  id: number;
  mission_id: number;
  run_id?: number | null;
  role: 'user' | 'sentinel' | 'system';
  body: string;
  action?: SentinelAction | null;
  /** Route options, followers and the recommendation, so a card survives a reload. */
  payload?: {
    kind?: SentinelAction;
    options?: RouteOption[];
    recommend?: string | null;
    intent?: 'follow' | 'threat' | 'status' | 'general' | null;
    threat_level?: string | null;
    threat_level_reason?: string | null;
    followers?: SentinelFollower[];
  } | null;
  created_by?: string | null;
  created_at: string;
}

/** One way of going, as the routing engine costed it. */
export interface RouteOption {
  index?: number;
  label?: string;
  geometry: GeoJSON.LineString;
  distance?: number;
  duration?: number;
  delta_duration_s?: number;
  delta_distance_m?: number;
  corridor_score?: number | null;
  /** Detection routes only: decisive turns, which is the whole product. */
  turns?: number;
  diagnostic?: boolean;
  /** Corrections only: whether this way back uses the road that was scanned. */
  rejoins_plan?: boolean;
  clears?: Array<{ event_id: number; label?: string; closest_approach_m: number }>;
  still_exposed?: Array<{ event_id: number; label?: string; closest_approach_m: number }>;
  /** Raw Mapbox legs; forwarded on confirm so stored directions match the new line. */
  legs?: unknown[];
}

export async function getMissionChat(missionId: number): Promise<MissionChatTurn[]> {
  const response = await axios.get(`${baseUrl}/missions/${missionId}/chat`, {
    headers: getAuthHeaders(),
  });
  return response.data?.turns || [];
}

export async function clearMissionChat(missionId: number): Promise<void> {
  await axios.delete(`${baseUrl}/missions/${missionId}/chat`, { headers: getAuthHeaders() });
}

/**
 * Have Sentinel carry out one of the manoeuvres end to end.
 *
 * Everything after the click happens without further prompting: it finds the
 * convoy, gathers what it is avoiding, asks the routing engine and reads the
 * result back. It does not apply anything — the route changes only when a
 * person picks an option.
 */
export async function runSentinelAction(
  missionId: number,
  action: SentinelAction,
  options: { detour_m?: number; patience_s?: number } = {},
): Promise<{
  action: SentinelAction;
  options: RouteOption[];
  recommend?: string | null;
  turns: MissionChatTurn[];
}> {
  const response = await axios.post(
    `${baseUrl}/missions/${missionId}/sentinel/action`,
    { action, ...options },
    { headers: getAuthHeaders() },
  );
  return response.data;
}

/** Apply a chosen option. The only call here that changes where the convoy goes. */
export async function applyRoute(
  missionId: number,
  option: RouteOption,
  clearedEventIds: number[] = [],
  name?: string,
  extras?: { reason?: string; spoken?: string; profile?: string },
): Promise<void> {
  await axios.post(
    `${baseUrl}/routing/reroute/confirm`,
    {
      mission_id: missionId,
      geometry: option.geometry,
      stats: { distance: option.distance, duration: option.duration },
      name,
      cleared_event_ids: clearedEventIds,
      // Keeps stored turn-by-turn in step with the new geometry, so
      // handsets don't speak the old route's manoeuvres.
      legs: option.legs,
      reason: extras?.reason,
      spoken: extras?.spoken,
      profile: extras?.profile,
    },
    { headers: getAuthHeaders() },
  );
}

/** Close the active run. A rehearsal returns the mission to the board. */
export async function endMissionRun(
  missionId: number,
  state: 'complete' | 'abandoned' = 'complete',
): Promise<Mission> {
  const response = await axios.post(
    `${baseUrl}/missions/${missionId}/runs/end`,
    { state },
    { headers: getAuthHeaders() },
  );
  return response.data;
}

/**
 * Sentinel's output for a mission.
 *
 * Shares the packet workspace's tables and its review lifecycle: everything the
 * model writes lands as a `draft` and only an analyst-accepted finding reaches a
 * report. That is the same Accept button in the same queue, deliberately, so an
 * analyst does not have to remember which workspace a finding came from.
 */
export interface MissionInsight {
  id: number;
  mission_id: number;
  run_id?: number | null;
  title: string;
  finding: string;
  category?: string | null;
  origin: 'ai' | 'analyst';
  validation: 'draft' | 'accepted' | 'rejected';
  confidence?: string | null;
  created_by?: string | null;
  created_at: string;
  validated_by?: string | null;
  validated_at?: string | null;
}

export interface MissionAfterAction {
  executive_summary: string;
  key_findings: string;
  pol_assessment: string;
  gaps: string;
  confidence: string;
}

export async function listMissionInsights(missionId: number): Promise<MissionInsight[]> {
  const response = await axios.get(`${baseUrl}/missions/${missionId}/sentinel/insights`, {
    headers: getAuthHeaders(),
  });
  return response.data.insights || [];
}

/** Ask Sentinel to draft findings. Drafts only; nothing is published by this. */
export async function draftMissionInsights(missionId: number): Promise<MissionInsight[]> {
  const response = await axios.post(
    `${baseUrl}/missions/${missionId}/sentinel/insights`,
    {},
    { headers: getAuthHeaders() },
  );
  return response.data.insights || [];
}

export async function validateMissionInsight(
  missionId: number,
  insightId: number,
  validation: 'accepted' | 'rejected' | 'draft',
): Promise<MissionInsight> {
  const response = await axios.post(
    `${baseUrl}/missions/${missionId}/sentinel/insights/${insightId}/validate`,
    { validation },
    { headers: getAuthHeaders() },
  );
  return response.data;
}

/**
 * Draft the run report. Returned for reading and editing; `publish` is what
 * writes it, and that stays a separate decision a human makes.
 */
export async function draftAfterAction(
  missionId: number,
  options: { run_id?: number; publish?: boolean } = {},
): Promise<{ draft: MissionAfterAction }> {
  const response = await axios.post(
    `${baseUrl}/missions/${missionId}/sentinel/after-action`,
    { run_id: options.run_id, publish: !!options.publish },
    { headers: getAuthHeaders() },
  );
  return response.data;
}

/**
 * One turn of the conversation. Both turns come back so the thread can append
 * without refetching, and they are already stored server-side.
 */
export async function askMissionSentinel(
  missionId: number,
  question: string,
): Promise<{ answer: string; turns: MissionChatTurn[] }> {
  const response = await axios.post(
    `${baseUrl}/missions/${missionId}/sentinel/ask`,
    { question },
    { headers: getAuthHeaders() },
  );
  return { answer: response.data.answer || '', turns: response.data.turns || [] };
}

/** Fetch the run report as a file. PDF falls back to 501 where WeasyPrint is absent. */
export async function downloadRunReport(
  missionId: number,
  options: { run_id?: number; format?: 'html' | 'pdf' } = {},
): Promise<Blob> {
  const response = await axios.get(`${baseUrl}/missions/${missionId}/export`, {
    params: { run_id: options.run_id, format: options.format || 'html' },
    headers: getAuthHeaders(),
    responseType: 'blob',
  });
  return response.data;
}

export async function getMissionRouteEvents(
  missionId: number,
  hours = 48,
): Promise<MissionRouteStatus> {
  const response = await axios.get(`${baseUrl}/missions/${missionId}/route-events`, {
    params: { hours },
    headers: getAuthHeaders(),
  });
  return response.data;
}

export interface MissionFieldFace {
  label?: string | null;
  cluster_id?: number | string | null;
  confidence?: number | null;
  watchlisted?: boolean;
}

export interface MissionFieldReport {
  id: string;
  device_id?: string | null;
  device_owner?: string | null;
  kind?: string | null;
  identifier?: string | null;
  notes?: string | null;
  latitude: number;
  longitude: number;
  accuracy_meters?: number | null;
  observed_at?: string | null;
  has_photo: boolean;
  has_annotated?: boolean;
  photo_url?: string | null;
  ocr_text?: string | null;
  caption?: string | null;
  scene?: string | null;
  objects?: string[];
  tags?: string[];
  face_status?: string | null;
  faces?: MissionFieldFace[];
  face_matches?: string[];
  /** A named face on this photo is already on the watchlist. */
  watchlisted?: boolean;
}

export interface MissionDevicePosition {
  device_id: string;
  latitude: number;
  longitude: number;
  last_fix_at?: string | null;
}

export async function listMissionDevicePositions(
  missionId: number,
): Promise<MissionDevicePosition[]> {
  const response = await axios.get(`${baseUrl}/missions/${missionId}/device-positions`, {
    headers: getAuthHeaders(),
  });
  return response.data.devices || [];
}

export interface MissionDwell {
  id: number | string;
  device_id?: string | null;
  label: string;
  place_name?: string | null;
  place_address?: string | null;
  latitude: number;
  longitude: number;
  start_at?: string | null;
  end_at?: string | null;
  duration_s?: number | null;
  source: 'own' | 'observed';
  own: boolean;
}

export type MissionSignalScope = 'run' | 'observer' | 'area';

export interface MissionSignalCluster {
  latitude: number;
  longitude: number;
  sightings: number;
}

export interface MissionSignalEmitter {
  identifier: string;
  normalized: string;
  kind: 'wifi' | 'ble' | string;
  name?: string | null;
  sightings: number;
  places: number;
  first_seen?: string | null;
  last_seen?: string | null;
  device_id?: string | null;
  latitude?: number | null;
  longitude?: number | null;
  clusters: MissionSignalCluster[];
  own_kit: boolean;
  watchlisted: boolean;
  watchlist_id?: number | null;
  watchlist_label?: string | null;
}

export interface MissionSignalSurvey {
  scanned_at: string;
  scope: MissionSignalScope;
  observer_id?: string | null;
  cluster_m: number;
  since?: string | null;
  until?: string | null;
  reason?: string | null;
  observers: { observer_id: string; label: string }[];
  emitters: MissionSignalEmitter[];
  heard: number;
  multi_count: number;
}

export async function surveyMissionSignals(
  missionId: number,
  scope: MissionSignalScope = 'run',
  observerId?: string,
): Promise<MissionSignalSurvey> {
  const response = await axios.get(`${baseUrl}/missions/${missionId}/signals`, {
    params: { scope, observer_id: observerId || undefined },
    headers: getAuthHeaders(),
  });
  return response.data;
}

export async function promoteRouteEvent(
  missionId: number,
  eventId: number,
  reason?: string,
): Promise<{ watchlist_id: number; already_present: boolean; label: string }> {
  const response = await axios.post(
    `${baseUrl}/missions/${missionId}/route-events/${eventId}/promote`,
    { reason },
    { headers: getAuthHeaders() },
  );
  return response.data;
}

export async function promoteMissionSignal(
  missionId: number,
  body: {
    identifier: string;
    signal_type?: string;
    identifier_type?: string;
    name?: string;
    reason?: string;
  },
): Promise<{ watchlist_id: number; already_present: boolean; label: string }> {
  const response = await axios.post(
    `${baseUrl}/missions/${missionId}/signals/promote`,
    body,
    { headers: getAuthHeaders() },
  );
  return response.data;
}

export async function listMissionDwells(missionId: number): Promise<MissionDwell[]> {
  const response = await axios.get(`${baseUrl}/missions/${missionId}/dwells`, {
    headers: getAuthHeaders(),
  });
  return response.data?.dwells || [];
}

export async function listMissionFieldReports(
  missionId: number,
): Promise<MissionFieldReport[]> {
  const response = await axios.get(`${baseUrl}/missions/${missionId}/field-reports`, {
    headers: getAuthHeaders(),
  });
  return response.data.reports || [];
}

/** Run the deviation check now instead of waiting for the next scheduler tick. */
export async function runMissionMonitor(
  missionId: number,
  hours = 48,
): Promise<MissionRouteStatus> {
  const response = await axios.post(`${baseUrl}/missions/${missionId}/monitor`, null, {
    params: { hours },
    headers: getAuthHeaders(),
  });
  return response.data;
}

export async function acknowledgeRouteEvents(
  missionId: number,
  eventId?: number,
): Promise<MissionRouteStatus> {
  const response = await axios.post(`${baseUrl}/missions/${missionId}/route-events/ack`, null, {
    params: eventId ? { event_id: eventId } : undefined,
    headers: getAuthHeaders(),
  });
  return response.data;
}

/** Speak a message on the vehicle. The crew's ack comes back on the same event. */
export async function sendVehicleAlert(
  missionId: number,
  message: string,
  severity: 'warning' | 'critical' = 'warning',
): Promise<MissionRouteStatus> {
  const response = await axios.post(
    `${baseUrl}/missions/${missionId}/route-events/notify`,
    { message, severity },
    { headers: getAuthHeaders() },
  );
  return response.data;
}

// ------------------------------------------------------ friendly RF baseline

export interface BaselineEntry {
  id: number;
  identifier: string;
  normalized_identifier: string;
  signal_type: 'wifi' | 'bluetooth' | string;
  name?: string | null;
  /** sweep = learned automatically, manual = trusted by hand, registry = fleet kit. */
  source: 'sweep' | 'manual' | 'registry' | string;
  sighting_count: number;
  note?: string | null;
  captured_by?: string | null;
  captured_at: string;
}

export interface MissionBaseline {
  state: 'none' | 'capturing' | 'ready';
  started_at?: string | null;
  ready_at?: string | null;
  window_s: number;
  entries: BaselineEntry[];
  captured?: number;
  from_registry?: number;
  observers?: number;
  reason?: string;
}

/** An emitter the convoy can currently hear, offered for trusting. */
export interface BaselineCandidate {
  identifier: string;
  name?: string | null;
  signal_type: 'wifi' | 'bluetooth' | string;
  manufacturer_id?: number | null;
  sightings: number;
  trusted: boolean;
}

export async function getMissionBaseline(missionId: number): Promise<MissionBaseline> {
  const response = await axios.get(`${baseUrl}/missions/${missionId}/baseline`, {
    headers: getAuthHeaders(),
  });
  return response.data;
}

/** Learn the convoy's own radios now rather than waiting for departure. */
export async function captureMissionBaseline(
  missionId: number,
  seconds = 120,
): Promise<MissionBaseline> {
  const response = await axios.post(`${baseUrl}/missions/${missionId}/baseline/capture`, null, {
    params: { seconds },
    headers: getAuthHeaders(),
  });
  return response.data;
}

export async function getBaselineCandidates(
  missionId: number,
  seconds = 120,
): Promise<BaselineCandidate[]> {
  const response = await axios.get(`${baseUrl}/missions/${missionId}/baseline/candidates`, {
    params: { seconds },
    headers: getAuthHeaders(),
  });
  return response.data;
}

export interface TrustIdentifierBody {
  identifier: string;
  signal_type?: string;
  name?: string | null;
  manufacturer_id?: number | null;
  note?: string | null;
  /** Also register it permanently, so the next run does not relearn it. */
  register_to_entity?: string | null;
  kit_type?: string | null;
}

export async function trustIdentifier(
  missionId: number,
  body: TrustIdentifierBody,
): Promise<MissionBaseline> {
  const response = await axios.post(`${baseUrl}/missions/${missionId}/baseline/trust`, body, {
    headers: getAuthHeaders(),
  });
  return response.data;
}

export async function trustIdentifiers(
  missionId: number,
  items: TrustIdentifierBody[],
): Promise<MissionBaseline> {
  const response = await axios.post(
    `${baseUrl}/missions/${missionId}/baseline/trust-batch`,
    { items },
    { headers: getAuthHeaders() },
  );
  return response.data;
}

export async function untrustIdentifier(
  missionId: number,
  identifier: string,
): Promise<MissionBaseline> {
  const response = await axios.delete(`${baseUrl}/missions/${missionId}/baseline`, {
    params: { identifier },
    headers: getAuthHeaders(),
  });
  return response.data;
}

export async function saveMissionRoute(
  missionId: number,
  body: {
    profile: string;
    options?: Record<string, unknown>;
    geometry?: GeoJSON.LineString | null;
    stats?: MissionRouteStats;
    /** Omit to leave any stored alternate alone. */
    alternate?: Omit<MissionAlternateRoute, 'signature' | 'saved_at'> | null;
    clear_alternate?: boolean;
    /** Turn list for this geometry so a mid-run edit does not silence the phone. */
    legs?: unknown[];
  },
): Promise<Mission> {
  const response = await axios.put(`${baseUrl}/missions/${missionId}/route`, body, { headers: getAuthHeaders() });
  return response.data;
}

export async function saveNamedRoute(
  missionId: number,
  body: {
    id?: string;
    label: string;
    waypoints: Array<{ label?: string; latitude: number; longitude: number; notes?: string }>;
    profile: string;
    options?: Record<string, unknown>;
    geometry?: GeoJSON.LineString | null;
    stats?: MissionRouteStats;
    alternate?: Omit<MissionAlternateRoute, 'signature' | 'saved_at'> | null;
    clear_alternate?: boolean;
  },
): Promise<Mission> {
  const response = await axios.put(
    `${baseUrl}/missions/${missionId}/named-routes`,
    body,
    { headers: getAuthHeaders() },
  );
  return response.data;
}

export async function deleteNamedRoute(missionId: number, routeId: string): Promise<Mission> {
  const response = await axios.delete(
    `${baseUrl}/missions/${missionId}/named-routes/${encodeURIComponent(routeId)}`,
    { headers: getAuthHeaders() },
  );
  return response.data;
}

export async function activateNamedRoute(missionId: number, routeId: string): Promise<Mission> {
  const response = await axios.post(
    `${baseUrl}/missions/${missionId}/route/activate`,
    { route_id: routeId },
    { headers: getAuthHeaders() },
  );
  return response.data;
}

/**
 * Make the alternate live and the live route the alternate.
 *
 * An exchange, not a replacement, so an operator who switches because of
 * something ahead and finds the alternate worse has one click back rather than
 * a replan under pressure.
 */
export async function swapToAlternate(missionId: number): Promise<Mission> {
  const response = await axios.post(
    `${baseUrl}/missions/${missionId}/route/swap`,
    {},
    { headers: getAuthHeaders() },
  );
  return response.data;
}

/**
 * Keep the planner's findings with the mission so they outlive the planner.
 *
 * Sections are merged server-side, so sending only the one that changed does
 * not discard the others.
 */
export async function saveMissionRouteIntel(
  missionId: number,
  body: {
    directions?: Record<string, any>;
    places?: Record<string, any>;
    events?: Record<string, any>;
    weather?: Record<string, any>;
    corridor?: Record<string, any>;
  },
): Promise<Mission> {
  const response = await axios.put(
    `${baseUrl}/missions/${missionId}/route-intel`,
    body,
    { headers: getAuthHeaders() },
  );
  return response.data;
}

export default {
  listMissions,
  getMission,
  createMission,
  updateMission,
  deleteMission,
  replaceMissionWaypoints,
  replaceMissionAssignees,
  replaceMissionGeofences,
  getMissionGeofenceActivity,
  getMissionRouteEvents,
  listMissionDwells,
  surveyMissionSignals,
  promoteMissionSignal,
  promoteRouteEvent,
  listMissionFieldReports,
  runMissionMonitor,
  acknowledgeRouteEvents,
  saveMissionRoute,
  saveMissionRouteIntel,
  listRosterEntities,
  getMissionBaseline,
  captureMissionBaseline,
  getBaselineCandidates,
  trustIdentifier,
  trustIdentifiers,
  untrustIdentifier,
};
